import React, { useEffect, useMemo, useState } from "react"
import {
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap"
import { EngDiagQuestionView } from "./EngDiagQuestionView"
import { EngDiagRichContent } from "./EngDiagRichContent"
import {
  getEngDiagAssignedPreview,
  getEngDiagPreviewQuestions,
} from "../services/eng-diag.service"
import type {
  EngDiagQuestion,
  EngDiagStudentAnswers,
} from "../types/eng-diag.types"

type Props = {
  isOpen: boolean
  onClose: () => void
  /** Assign-time source: a year, optionally narrowed to topics. Ignored when activityKey is set. */
  yearLevelId?: number
  yearLevelLabel: string
  subjectId?: number
  /** Only these topics; empty or omitted previews the whole year. */
  topicIds?: number[]
  /** An assigned test: previews its own frozen questions rather than the live year content. */
  activityKey?: string | null
}

function typeLabel(type: string): string {
  return type.replace(/_/g, " ")
}

/**
 * English Diagnostics preview — same modal chrome as math BmdPreviewModal.
 * MCQ uses the math-style answer list; other types use EngDiagQuestionView.
 */
export function EngDiagPreviewModal({
  isOpen,
  onClose,
  yearLevelId,
  yearLevelLabel,
  subjectId,
  topicIds = [],
  activityKey,
}: Props) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [questions, setQuestions] = useState<EngDiagQuestion[]>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [answers, setAnswers] = useState<EngDiagStudentAnswers>({})

  // Joined so the effect reruns on a different selection, not on a new array
  // holding the same ids.
  const topicKey = topicIds.join(",")

  useEffect(() => {
    if (!isOpen || (!activityKey && !yearLevelId)) return
    const controller = new AbortController()
    const topic_ids = topicKey ? topicKey.split(",").map(Number) : undefined
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        setActiveIndex(0)
        setAnswers({})
        const data = activityKey
          ? await getEngDiagAssignedPreview(
              { pk_activity_key: activityKey },
              controller.signal,
            )
          : await getEngDiagPreviewQuestions(
              { year_level_id: yearLevelId!, subject_id: subjectId, topic_ids },
              controller.signal,
            )
        if (data.error) throw new Error(data.error)
        setQuestions(data.questions || [])
      } catch (e: any) {
        if (controller.signal.aborted) return
        setError(e?.message || "Failed to load preview")
        setQuestions([])
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    })()
    return () => controller.abort()
  }, [isOpen, yearLevelId, subjectId, topicKey, activityKey])

  const activeQuestion = useMemo(
    () => questions[activeIndex] || null,
    [questions, activeIndex],
  )

  const goPrevious = () => {
    setActiveIndex(prev => Math.max(prev - 1, 0))
    setAnswers({})
  }
  const goNext = () => {
    setActiveIndex(prev => Math.min(prev + 1, questions.length - 1))
    setAnswers({})
  }

  const handleJump = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseInt(event.target.value, 10)
    if (!Number.isNaN(next)) {
      setActiveIndex(next)
      setAnswers({})
    }
  }

  const renderMcqAnswers = (question: EngDiagQuestion) => {
    const choices = question.options?.choices || []
    return (
      <div>
        <h6 className="text-dark fw-700 mb-3">Answers</h6>
        {choices.length ? (
          <ul className="list-unstyled mb-0">
            {choices.map((choice: any, answerIndex: number) => {
              const isCorrect = !!choice.isAnswer
              const optionLabel = String.fromCharCode(65 + answerIndex)
              return (
                <li
                  key={choice.id}
                  className={`p-3 mb-3 ${
                    isCorrect ? "correct-ans" : "bg-white border rounded"
                  }`}
                >
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div className="d-flex align-items-start gap-3 flex-grow-1">
                      <div
                        className={`fw-800 font-size-16 ${
                          isCorrect ? "text-success" : "text-dark"
                        }`}
                        style={{ minWidth: 28 }}
                      >
                        {optionLabel}.
                      </div>
                      {/* An option can BE a clip - the Reception letter-sound questions are
                          four recordings and no text - and LCMS stores one as an empty
                          placeholder span, so these previewed as four blank rows. */}
                      <EngDiagRichContent
                        className="question_container flex-grow-1"
                        html={choice.content || ""}
                      />
                    </div>
                    {isCorrect ? (
                      <span className="badge bg-success text-white">
                        Correct
                      </span>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
        ) : (
          <div className="text-muted">No answers available for this question.</div>
        )}
      </div>
    )
  }

  const questionCount = questions.length

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="xl"
      scrollable
      centered
      className="remove-bd preview-activity"
    >
      <ModalHeader
        toggle={onClose}
        className="modal-header--background border-0"
      >
        <div className="w-100 pe-4">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h5 className="mb-1 text-white fw-800">Preview diagnostics</h5>
              <div className="luminous-wht font-size-13">
                {yearLevelLabel || "Diagnostics"}
                {activityKey
                  ? " · as assigned"
                  : topicIds.length > 0
                  ? " · selected clusters"
                  : " · whole year"}
              </div>
            </div>
            {!loading && questionCount > 0 ? (
              <div className="text-md-end">
                <div className="text-yellow fw-800 font-size-20">
                  {questionCount}
                </div>
                <div className="luminous-wht font-size-12">Questions</div>
              </div>
            ) : null}
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="bg-supermild">
        <div className="assessment-width-hold">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-3 text-muted">Loading preview…</div>
            </div>
          ) : null}

          {error && !loading ? (
            <div className="card shadow-sm border-0">
              <div className="card-body p-4 text-danger">{error}</div>
            </div>
          ) : null}

          {!loading && !error && questions.length > 0 ? (
            <div className="assessement-section">
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-end flex-wrap gap-3">
                    <div>
                      <Label
                        for="eng-diag-preview-question-select"
                        className="text-dark fw-700 mb-2"
                      >
                        Jump to question
                      </Label>
                      <Input
                        id="eng-diag-preview-question-select"
                        type="select"
                        value={String(activeIndex)}
                        onChange={handleJump}
                        style={{ minWidth: "260px" }}
                      >
                        {questions.map((q, index) => (
                          <option key={q.id} value={String(index)}>
                            Question {index + 1}: {typeLabel(q.type)}
                          </option>
                        ))}
                      </Input>
                    </div>
                    <div className="text-md-end">
                      <div className="fw-700 text-dark">
                        {activeIndex + 1} of {questions.length}
                      </div>
                      {activeQuestion ? (
                        <div className="text-muted font-size-13 text-capitalize">
                          Type: {typeLabel(activeQuestion.type)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {activeQuestion ? (
                <div className="card shadow-sm border-0">
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                      <div>
                        <h5 className="text-dark fw-800 mb-1">
                          Question {activeIndex + 1}
                        </h5>
                        <p className="mb-0 text-muted text-capitalize">
                          {typeLabel(activeQuestion.type)}
                        </p>
                      </div>
                      <div className="text-md-end">
                        <div className="fw-700 text-dark">
                          {activeIndex + 1} of {questions.length}
                        </div>
                      </div>
                    </div>

                    {/* Instruction and content are rich text from the LCMS
                        editor, so they render as HTML here and in
                        EngDiagQuestionView, which shows them for every
                        other type. */}
                    {activeQuestion.type === "mcq" ? (
                      <>
                        {activeQuestion.instruction ? (
                          <EngDiagRichContent
                            className="fw-600 mb-2"
                            html={activeQuestion.instruction}
                          />
                        ) : null}
                        <div className="question_container mb-4">
                          <EngDiagRichContent html={activeQuestion.content || ""} />
                        </div>
                        {renderMcqAnswers(activeQuestion)}
                      </>
                    ) : (
                      <EngDiagQuestionView
                        question={activeQuestion}
                        value={answers}
                        onChange={setAnswers}
                      />
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {!loading && !error && questions.length === 0 ? (
            <div className="card shadow-sm border-0">
              <div className="card-body p-4 text-muted">
                No preview questions available.
              </div>
            </div>
          ) : null}
        </div>
      </ModalBody>

      <ModalFooter className="border-0 bg-supermild pt-0">
        <div className="assessment-width-hold w-100 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <button
            type="button"
            className="btn-cst-secondary-md"
            onClick={goPrevious}
            disabled={activeIndex === 0 || questions.length === 0}
          >
            Previous
          </button>
          <div className="d-flex gap-2 flex-wrap">
            <button
              type="button"
              className="btn-cst-secondary-md"
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              className="btn-cst-danger-md"
              onClick={goNext}
              disabled={
                questions.length === 0 ||
                activeIndex === questions.length - 1
              }
            >
              Next
            </button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default EngDiagPreviewModal
